import type { AskSurface } from './askSuggestions'
import type { AskEntity } from './ShellContext'
import type { AskContextHint } from '../services/api'

/**
 * The one place a shell pin becomes a wire hint (MAR-174 §3.2).
 *
 * The shell's `AskEntity` and the server's `AskContextHint` look alike and are
 * not the same object, which is exactly the kind of near-miss that gets copied
 * around a codebase until one copy is wrong. So there is one converter, every
 * send goes through it, and a test asserts the narrowing rather than a comment
 * asking for it.
 *
 * ⚠️ **`label` MUST NOT be sent, and the reason is not tidiness.** `AskEntity.label`
 * is display text for the merchant's pin banner, and `custName` falls back to
 * the customer's EMAIL ADDRESS (`components/knowledge/knowledgeIndex.tsx`, `custName`). `AskContextHintSchema`
 * is `.strict()`, so a label on the wire is a **400 on every single turn** —
 * loud, not silent, which is the point of the strictness. But the reason it is
 * strict is that the model must be given the server's own rendering of a record
 * it verified, never a shopper's address arriving from a client field.
 *
 * ⚠️ **`workspaceId` MUST NOT be sent either.** It is on the shell entity
 * because the pin is scoped at the read; on the wire the server derives the
 * workspace from the authorizer and ignores the body. Sending it is harmless
 * only until somebody trusts it — and `.strict()` rejects it, so this converter
 * is what stops the pin being spread onto a request wholesale.
 *
 * ⚠️ **No `view` is produced yet, deliberately.** The v6 surfaces have no
 * window or filter state the shell can see, so there is nothing to publish; a
 * hint carrying an invented window would be a claim about the merchant's screen
 * that nothing supports. `AskContextHint.view` exists on the type because the
 * server accepts it and Phase C will fill it.
 */
export function toContextHint(
  surface: AskSurface | null,
  entity: AskEntity | null
): AskContextHint | undefined {
  // §3.2 makes `surface` required, so a screen the contract does not name (Docs,
  // Account, Admin) sends no hint at all — including when a record is somehow
  // pinned there. Half a hint is not a smaller hint; it is an invalid one, and
  // the strict schema would refuse it.
  if (!surface) return undefined

  // Named field by field, never spread. A spread of `entity` would carry
  // `label` and `workspaceId` through the day either is added to or renamed on
  // the shell type, and the failure would be a 400 on every turn for whoever
  // shipped it next.
  if (!entity) return { surface }

  return { surface, entity: { type: entity.type, id: entity.id } }
}
